import { useEffect, useCallback } from "react";
import { useLocation } from "react-router-dom";
import { getCookieConsent } from "./CookieBanner";

// Microsoft Clarity — CONSENT-GATED and ROUTE-SCOPED.
// Compliance note: Clarity's terms prohibit collecting Medical Information / PHI.
// RapidCare's /app/* clinical tools (calculators, drug interactions, ICD-10 AI
// search) can contain such data, so Clarity is NEVER started on /app/* routes.
// It runs ONLY on public marketing/funnel pages, and ONLY after the user grants
// analytics consent via the cookie banner. It does not send any GA4 events.
const CLARITY_ID = process.env.REACT_APP_CLARITY_PROJECT_ID;

let scriptInjected = false;

function injectClarity(id) {
  // Only one Clarity source, ever: skip if already injected (flag), if the
  // global exists, or if a Clarity tag is already present in the DOM.
  if (scriptInjected || window.clarity || document.querySelector('script[src*="clarity.ms/tag"]')) {
    scriptInjected = true;
    return;
  }
  scriptInjected = true;
  (function (c, l, a, r, i, t, y) {
    c[a] = c[a] || function () { (c[a].q = c[a].q || []).push(arguments); };
    t = l.createElement(r); t.async = 1; t.src = "https://www.clarity.ms/tag/" + i;
    y = l.getElementsByTagName(r)[0]; y.parentNode.insertBefore(t, y);
  })(window, document, "clarity", "script", id);
}

// Marketing pages = everything that is NOT an in-app clinical route (/app/*).
function isMarketingPath(pathname) {
  return !pathname.startsWith("/app");
}

// Network-level guard — the ONLY airtight way to guarantee zero clarity.ms
// requests on /app/* routes. Clarity's stop()/consentv2() are forward-looking
// and cannot cancel already-buffered beacons, so we intercept fetch / XHR /
// sendBeacon and no-op any clarity.ms request WHILE on a clinical /app/* route.
// It affects clarity.ms requests only, and only on /app/* — everything else is
// passed through untouched. Installed once for the app's lifetime.
let guardInstalled = false;
function isClarityUrl(url) {
  try { return /clarity\.ms/i.test(String(url)); } catch (e) { return false; }
}
function blockClarity(url) {
  return isClarityUrl(url) && window.location.pathname.startsWith("/app");
}
function installClarityNetworkGuard() {
  if (guardInstalled || typeof window === "undefined") return;
  guardInstalled = true;

  const origFetch = window.fetch;
  if (origFetch) {
    window.fetch = function (input, init) {
      try {
        const url = typeof input === "string" ? input : (input && input.url);
        if (blockClarity(url)) return Promise.resolve(new Response(null, { status: 204 }));
      } catch (e) {}
      return origFetch.apply(this, arguments);
    };
  }

  if (navigator.sendBeacon) {
    const origBeacon = navigator.sendBeacon.bind(navigator);
    navigator.sendBeacon = function (url, data) {
      try { if (blockClarity(url)) return true; } catch (e) {}
      return origBeacon(url, data);
    };
  }

  const XHR = window.XMLHttpRequest;
  if (XHR && XHR.prototype) {
    const origOpen = XHR.prototype.open;
    const origSend = XHR.prototype.send;
    XHR.prototype.open = function (method, url) {
      try { this.__rcBlockClarity = blockClarity(url); } catch (e) { this.__rcBlockClarity = false; }
      return origOpen.apply(this, arguments);
    };
    XHR.prototype.send = function () {
      if (this.__rcBlockClarity) { try { this.abort(); } catch (e) {} return; }
      return origSend.apply(this, arguments);
    };
  }
}

export default function ClarityLoader() {
  const location = useLocation();

  // Install the network guard once, up front, so it's ready before any /app/* nav.
  useEffect(() => { installClarityNetworkGuard(); }, []);

  const evaluate = useCallback(() => {
    if (!CLARITY_ID) return;
    const consent = getCookieConsent();
    const granted = consent && consent.choice === "accept";
    const marketing = isMarketingPath(window.location.pathname);

    if (!granted) {
      // No analytics consent — make sure nothing is being collected.
      if (window.clarity) {
        try {
          window.clarity("consentv2", { ad_Storage: "denied", analytics_Storage: "denied" });
          window.clarity("stop");
        } catch (e) {}
      }
      return;
    }

    if (marketing) {
      injectClarity(CLARITY_ID);
      if (window.clarity) {
        try {
          window.clarity("consentv2", { ad_Storage: "granted", analytics_Storage: "granted" });
          window.clarity("start");
        } catch (e) {}
      }
    } else if (window.clarity) {
      // Entered a clinical /app/* page — deny consent FIRST (suppresses any
      // buffered/in-flight telemetry flush) then halt collection.
      try {
        window.clarity("consentv2", { ad_Storage: "denied", analytics_Storage: "denied" });
        window.clarity("stop");
      } catch (e) {}
    }
  }, []);

  // Re-evaluate on every route change.
  useEffect(() => { evaluate(); }, [location.pathname, evaluate]);

  // Re-evaluate immediately when the user accepts/rejects/withdraws consent
  // (custom event from CookieBanner, no navigation needed).
  useEffect(() => {
    const handler = () => evaluate();
    window.addEventListener("rcs-consent-updated", handler);
    // Cross-tab / external consent withdrawal — react to localStorage changes.
    const storageHandler = (e) => {
      if (!e || e.key === null || e.key === "rcs-cookie-consent-v2") evaluate();
    };
    window.addEventListener("storage", storageHandler);
    return () => {
      window.removeEventListener("rcs-consent-updated", handler);
      window.removeEventListener("storage", storageHandler);
    };
  }, [evaluate]);

  return null;
}
